import { usePilotAddressSuggestions } from "apis/pilot";
import { applyAddressSuggestion } from "utils/addressSuggest";
import { IoLocationOutline } from "react-icons/io5";
import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp, FiClock, FiPaperclip, FiSquare, FiX } from "react-icons/fi";
import AutoResizeTextarea from "components/ui/AutoResizeTextarea";
import Button from "components/ui/Button";
import { DictationButton } from "./DictationButton";
import { DictationWaveForm } from "./DictationWaveForm";

// The agent web app's Sandy bar, as the admin Pilot page uses it.

const Spinner = () => (
  <span className="block h-5 w-5 animate-spin rounded-full border-2 border-darkergray/20 border-t-darkergray" />
);

const circleButton =
  "flex h-11 w-11 flex-none items-center justify-center rounded-full transition-all duration-200 active:scale-90";

// The launch look, as the app draws it: the bar in a ring whose colours travel
// round it (a bright band over a faint ring).
const LAUNCH_FAINT = "rgba(139,124,246,0.22)";
const LAUNCH_SWEEP = `conic-gradient(${LAUNCH_FAINT} 0%, ${LAUNCH_FAINT} 40%, #6F8CF5 58%, #E9B14E 74%, #E7739F 88%, ${LAUNCH_FAINT} 100%)`;
const LAUNCH_SPIN = `@keyframes sandyLaunchSpin { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }`;

export default function PilotComposer({
  value,
  onChangeText,
  onSend,
  placeholder,
  isSending = false,
  onStop,
  // Shows the clock that opens past conversations.
  onOpenHistory,
  onFocusChange,
  // Rendered directly above the bar — the suggestion lists.
  children,
  inputRef,
  // Sees each key first, for a list above the bar; a key it prevents is not sent.
  onKeyDown,
  suggestAddresses = false,
  // A launch: the bar in the colour ring.
  launch = false,
  // Files to send with the message: `onAttachFiles(files)` gets those picked with the paperclip, `attachments`
  // ({ id, name }) are shown above the bar, each removed with `onRemoveAttachment(id)`, and sent even with no text.
  onAttachFiles,
  attachments = [],
  onRemoveAttachment,
  isAttaching = false,
  acceptFiles,
}) {
  const localRef = useRef(null);
  const composerRootRef = useRef(null);
  const textInputRef = inputRef ?? localRef;
  const fileInputRef = useRef(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const takenSuggestion = useRef(null);

  // DictationButton fires `onSend` from a timeout holding the closure it was
  // rendered with, so reading `value` directly would send the text as it was
  // before the transcript landed.
  const latestValueRef = useRef(value);
  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  const { addresses: addressSuggestions, matchedText } = usePilotAddressSuggestions(
    { text: value },
    {
      enabled:
        suggestAddresses &&
        isFocused &&
        !isSending &&
        value !== takenSuggestion.current,
    },
  );

  const showAddressSuggestions =
    suggestAddresses &&
    isFocused &&
    !isSending &&
    !isTranscribing &&
    value !== takenSuggestion.current &&
    addressSuggestions.length > 0;

  const handleSend = () => {
    const trimmed = (latestValueRef.current || "").trim();
    if ((!trimmed && !attachments.length) || isSending || isAttaching) return;
    onSend(trimmed);
    onChangeText("");
    textInputRef.current?.blur();
  };

  const takeAddressSuggestion = (suggestion) => {
    const next = applyAddressSuggestion(value, matchedText, suggestion);
    takenSuggestion.current = next;
    onChangeText(next);
    textInputRef.current?.focus();
  };

  const hasText = (value || "").trim().length > 0;
  const canSend = hasText || attachments.length > 0;

  return (
    <div
      ref={composerRootRef}
      className="relative w-full"
      onBlurCapture={(event) => {
        if (composerRootRef.current?.contains(event.relatedTarget)) return;
        setIsFocused(false);
        onFocusChange?.(false);
      }}
    >
      {children}

      {showAddressSuggestions && (
        <div className="mb-2 overflow-hidden rounded-2xl border border-lightgray bg-surface shadow-sm">
          {addressSuggestions.map((suggestion, index) => (
            <Button
              key={suggestion}
              type="button"
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => takeAddressSuggestion(suggestion)}
              // The agent app's outline-button (its icon drawn in its text colour), which the dashboard's own class of
              // that name draws differently.
              className={`flex w-full items-center gap-2.5 !rounded-none bg-surface !px-3.5 !py-3 text-left text-[12px] text-textPrimary transition-colors duration-200 hover:bg-surfaceSoft ${
                index === 0 ? "!border-0" : "!border-x-0 !border-b-0 !border-t !border-lightgray"
              }`}
            >
              <IoLocationOutline
                size={16}
                className="shrink-0 text-textPrimary"
              />
              <span className="truncate text-sm text-darkergray">
                {suggestion}
              </span>
            </Button>
          ))}
        </div>
      )}
      {(attachments.length > 0 || isAttaching) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((file) => (
            <span
              key={file.id}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-mediumgray bg-white py-1 pl-2.5 pr-1 text-xs text-darkergray"
            >
              <FiPaperclip size={12} className="flex-none" aria-hidden="true" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => onRemoveAttachment?.(file.id)}
                className="flex h-5 w-5 flex-none items-center justify-center rounded-full hover:bg-lightgray"
              >
                <FiX size={12} />
              </button>
            </span>
          ))}
          {isAttaching && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-mediumgray bg-white px-2.5 py-1 text-xs text-darkgray">
              <span className="block h-3 w-3 animate-spin rounded-full border-2 border-black/20 border-t-black" />
              Attaching…
            </span>
          )}
        </div>
      )}

      {/* The ring: the same element on or off, so turning it never remakes the bar or loses what is typed. */}
      <div
        className={launch ? "relative overflow-hidden rounded-[31px] p-[3px]" : undefined}
      >
        {launch && (
          <>
            <style>{LAUNCH_SPIN}</style>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 aspect-[1/1] w-[150%]"
              style={{ background: LAUNCH_SWEEP, animation: "sandyLaunchSpin 2.8s linear infinite" }}
            />
          </>
        )}
      <div
        className={`relative flex items-end gap-2 rounded-[28px] bg-lightgray px-2.5 py-2.5 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.12)] ${
          isTranscribing ? "ring-2 ring-darkergray/20" : ""
        }`}
      >
        {onAttachFiles && !isTranscribing && (
          <>
            <button
              type="button"
              aria-label="Attach files"
              onClick={() => fileInputRef.current?.click()}
              className={`${circleButton} bg-white text-darkergray hover:bg-lightgray`}
            >
              <FiPaperclip size={19} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptFiles}
              className="hidden"
              onChange={(event) => {
                const picked = [...event.target.files];
                event.target.value = "";
                if (picked.length) onAttachFiles(picked);
              }}
            />
          </>
        )}

        {isTranscribing ? (
          <div className="flex min-h-[44px] flex-1 items-center self-center">
            <DictationWaveForm isListening={isTranscribing} />
          </div>
        ) : (
          <div className="relative min-w-0 flex-1 self-center px-1.5 py-2">
            {/* `!` because the dashboard's base styles size every textarea at 14px, important. */}
            <AutoResizeTextarea
              ref={textInputRef}
              className="w-full bg-transparent !text-[15px] leading-5 text-darkergray placeholder:text-darkgray focus:outline-none"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChangeText(e.target.value)}
              onFocus={() => {
                setIsFocused(true);
                onFocusChange?.(true);
              }}
              onKeyDown={(e) => {
                onKeyDown?.(e);
                if (e.defaultPrevented) return;
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              minRows={1}
              maxRows={8}
            />
          </div>
        )}

        {!isTranscribing && hasText && (
          <button
            type="button"
            aria-label="Clear message"
            onClick={() => {
              onChangeText("");
              textInputRef.current?.focus();
            }}
            className={`${circleButton} bg-surface text-darkergray hover:bg-surfaceSoft`}
          >
            <FiX size={20} />
          </button>
        )}

        {onOpenHistory && !isTranscribing && (
          <button
            type="button"
            aria-label="Open past conversations"
            // Keeps focus in the textarea, so opening history never closes whatever the bar is showing above itself.
            onMouseDown={(e) => e.preventDefault()}
            onClick={onOpenHistory}
            className={`${circleButton} bg-surface text-darkergray hover:bg-surfaceSoft`}
          >
            <FiClock size={20} />
          </button>
        )}

        <DictationButton
          text={value}
          setText={onChangeText}
          onSend={handleSend}
          onListeningChange={(listening) => {
            setIsTranscribing(listening);
            if (listening) onFocusChange?.(true);
          }}
        />

        {!isTranscribing && isSending && onStop && (
          <button
            type="button"
            aria-label="Stop Pilot"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onStop}
            className={`${circleButton} bg-black text-white hover:bg-black/80`}
          >
            <FiSquare size={14} />
          </button>
        )}

        {!isTranscribing && canSend && !(isSending && onStop) && (
          <button
            type="button"
            aria-label="Send message"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSend}
            disabled={isSending || isAttaching}
            className={`${circleButton} ${
              isSending
                ? "cursor-default bg-surface"
                : "bg-black text-white hover:bg-black/80"
            }`}
          >
            {isSending ? <Spinner /> : <FiArrowUp size={18} />}
          </button>
        )}
      </div>
      </div>
    </div>
  );
}
